<template>
  <transition
    appear
    name="fade"
  >
    <div
      class="backdraw"
      v-if="store.estSablierVisible"
      role="alert"
      aria-live="assertive"
      aria-busy="true"
      @click.stop
      @mousedown.stop
      @keydown.stop
      tabindex="-1"
    >
      <div class="spinner">
        <BasicText
          size="body-xl"
          class="loading"
        >
          Chargement
        </BasicText>
        <div class="bounce1"></div>
        <div class="bounce2"></div>
        <div class="bounce3"></div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
  import { useSablierStore } from './useSablierStore'
  import { watch, onUnmounted, ref } from 'vue'

  const store = useSablierStore()
  const previousActiveElement = ref<HTMLElement | null>(null)

  const handleFocusTrap = (e: FocusEvent) => {
    if (!store.estSablierVisible) return

    const target = e.target as HTMLElement
    const backdraw = document.querySelector('.backdraw')

    if (backdraw && !backdraw.contains(target)) {
      e.preventDefault()
      e.stopPropagation()
      ;(backdraw as HTMLElement).focus()
    }
  }

  const cleanupFocus = () => {
    document.removeEventListener('focusin', handleFocusTrap, true)
    document.body.style.overflow = ''
    if (previousActiveElement.value && document.body.contains(previousActiveElement.value)) {
      previousActiveElement.value.focus()
      previousActiveElement.value = null
    }
  }

  const activateFocusTrap = () => {
    if (document.activeElement && document.activeElement instanceof HTMLElement) {
      previousActiveElement.value = document.activeElement
      document.activeElement.blur()
    }
    document.addEventListener('focusin', handleFocusTrap, true)
    document.body.style.overflow = 'hidden'
  }

  watch(
    () => store.estSablierVisible,
    (isVisible) => {
      if (isVisible) {
        activateFocusTrap()
      } else {
        cleanupFocus()
      }
    },
    { immediate: true },
  )

  onUnmounted(() => {
    cleanupFocus()
  })
</script>

<style lang="less" scoped>
  .backdraw.fade-enter-active {
    transition-delay: 20ms;
  }
  .backdraw.fade-leave-active {
    transition-delay: 200ms;
  }
  .backdraw {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.5);
    pointer-events: all;
    z-index: 10000;
    cursor: wait;
  }

  .spinner {
    position: fixed;
    padding: 15px;
    text-align: center;
    background-color: rgba(255, 255, 255, 0.95);
    border-radius: 30px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    pointer-events: none;
    user-select: none;

    .loading {
      color: @primary-800;
      padding-bottom: 10px;
      display: block;
      max-width: 1000px;
      margin: 0;
      text-transform: uppercase;
    }
  }

  .spinner > div {
    width: 18px;
    height: 18px;
    background-color: @primary-800;
    border-radius: 100%;
    display: inline-block;
    -webkit-animation: sk-bouncedelay 1.4s infinite ease-in-out both;
    animation: sk-bouncedelay 1.4s infinite ease-in-out both;
  }

  .spinner .bounce1 {
    -webkit-animation-delay: -0.32s;
    animation-delay: -0.32s;
  }

  .spinner .bounce2 {
    -webkit-animation-delay: -0.16s;
    animation-delay: -0.16s;
  }

  @-webkit-keyframes sk-bouncedelay {
    0%,
    80%,
    100% {
      -webkit-transform: scale(0);
    }

    40% {
      -webkit-transform: scale(1);
    }
  }

  @keyframes sk-bouncedelay {
    0%,
    80%,
    100% {
      -webkit-transform: scale(0);
      transform: scale(0);
    }

    40% {
      -webkit-transform: scale(1);
      transform: scale(1);
    }
  }
</style>
